import { DateTime, DurationLike } from "luxon";
import { t } from "i18next";
import locale from "../constants/locale";
import type { Bot } from "../models";
import auth from "../stores/auth";

export type OriginCategory = { id: number; name: string; color: string; is_system: boolean; system_key?: string | null; origins_count: number };
export type OriginNumber = { bot_id: number; number: string; name: string; channel: string; timezone: string };
export type OriginOptions = {
  categories: OriginCategory[];
  numbers: OriginNumber[];
  origins: Pick<Origin, "id" | "name" | "meta_ad_id" | "ref_code">[];
};
export type Origin = {
  id: number; bot_id: number; name: string; type: "meta" | "custom" | "campaign"; category_id: number;
  category_name: string; category_color: string; ref_code: string | null; meta_ad_id: string | null;
  category_deleted_at?: string | null;
  business_number: string | null; prefilled_message: string | null; active_from: string | null;
  active_until: string | null; timezone: string; status: "active" | "archived";
  link: string | null; message: string | null; chats: number; replies?: number;
  campaign_id?: number | null; available_campaign_id?: number | null;
};
export type OriginRow = {
  origin_id: number | null; name: string | null; type: string | null; ref_code: string | null;
  meta_ad_id: string | null; business_number: string | null; category_id: number | null;
  category_name: string | null; category_color: string | null; chats: number; origins_count: number;
  outside_active_dates: number; campaign_id?: number | null;
};
export type Page<T> = { data: T[]; total: number; current_page: number; per_page: number };
export type OriginsReport = { total_chats: number; categories: OriginRow[]; rows: Page<OriginRow> };
export type OriginMetric = "all" | "first_touch" | "campaign_replies";
export type OriginFilters = { channel: string; from: string; to: string; category_id?: number; metric?: OriginMetric; search?: string };
export type OriginChat = {
  chat_id: number; first_name: string; last_name: string; sender_id: string;
  first_inbound_at: string; outside_active_dates: boolean; first_agent_id: string | null; extra_fields: Record<string, unknown>;
};

export async function originRequest<T>(bot: Pick<Bot, "id" | "key" | "password">, path: string, method = "GET", data: Record<string, unknown> = {}, signal?: AbortSignal): Promise<T> {
  const payload = { ...data, key: bot.key, password: bot.password };
  const query = new URLSearchParams();
  if (method === "GET") Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) query.set(key, String(value));
  });
  const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "application/json" };
  if (method === "PUT" || method === "DELETE" || (method === "POST" && ["", "/categories"].includes(path))) {
    headers.Authorization = `Bearer ${await auth.getBearerToken()}`;
  }
  const response = await fetch(`${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bot.id}/origins${path}${method === "GET" ? `?${query}` : ""}`, {
    method, headers, signal, ...(method !== "GET" ? { body: JSON.stringify(payload) } : {}),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(Object.values(error.errors || {}).flat().join(" ") || error.message || t(locale.chatOriginsLoadError));
  }
  return (path.endsWith("/export") ? response.blob() : response.json()) as Promise<T>;
}

export function originRange(timeline: string, dates: string[], timezone: string, now = DateTime.utc()): { from: string; to: string } | null {
  const local = now.setZone(timezone || "UTC");
  if (timeline === "custom") {
    if (!dates[0] || !dates[1] || dates[0] > dates[1]) return null;
    const from = DateTime.fromISO(dates[0], { zone: timezone || "UTC" });
    const to = DateTime.fromISO(dates[1], { zone: timezone || "UTC" });
    return from.isValid && to.isValid ? { from: from.startOf("day").toUTC().toISO(), to: to.endOf("day").toUTC().toISO() } : null;
  }
  const minus: Record<string, DurationLike> = { days: { days: 7 }, weeks: { weeks: 7 }, months: { months: 7 }, years: { years: 7 } };
  return {
    from: local.startOf("day").minus(minus[timeline] || {}).toUTC().toISO(),
    to: local.minus({ days: timeline === "today" ? 0 : 1 }).endOf("day").toUTC().toISO(),
  };
}

export function originLink(number: string, message: string, code: string): string {
  return `https://wa.me/${number}?text=${encodeURIComponent(`${message} (Ref: ${code})`.trim())}`;
}

export function downloadOriginFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
